import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from 'Hooks/useToast';

/**
 * The step payload of a wizard page whose every read and write answers with
 * the page's whole payload plus the wizard rail.
 *
 * Responses can land out of order (a refetch racing a write), so a payload is
 * applied only when it is newer than the last one applied. A write answered
 * after a newer response was applied re-reads the page, since that snapshot
 * may predate it.
 *
 * initialRequest replaces the opening GET, for a page that loads through a
 * write (a starter set offered on first open).
 */
export default function useStepPayload(dataUrl, onStatusChange, initialRequest = null) {
    const [data, setData] = useState(null);
    const toast = useToast();
    const nextRequest = useRef(0);
    const lastApplied = useRef(0);

    const send = useCallback(
        (request, pick = (response) => response, isRead = false) => {
            const id = ++nextRequest.current;

            return request().then((response) => {
                const payload = pick(response.data);

                if (id > lastApplied.current) {
                    lastApplied.current = id;
                    setData(payload);
                    onStatusChange(payload.wizard);
                } else if (!isRead) {
                    send(() => axios.get(dataUrl), undefined, true).catch(() => {});
                }

                return response.data;
            });
        },
        [dataUrl, onStatusChange],
    );

    const load = useCallback(
        () => send(() => axios.get(dataUrl), undefined, true).catch(() => toast.error($t('Something went wrong. Please try again.'))),
        [dataUrl, send],
    );

    useEffect(() => {
        if (initialRequest) {
            send(initialRequest, undefined, true).catch(() => load());
        } else {
            load();
        }
    }, [dataUrl]);

    return { data, send, load };
}
